import React, { Component } from 'react';

import { withTranslation } from 'react-i18next';
import ReactSVG from 'react-svg';

import ContactDetailsView from '@components//general/ContactDetailsView';
import ConsignmentView from '@components/consignment/ConsignmentView';
import CustomerView from '@components/customer/CustomerView';
import NotesView from '@components/note/NotesView';
import QuoteContainer from '@containers/administration/quote/QuoteContainer';
import QuoteFormContainer from '@containers/administration/quote/crud/QuoteFormContainer';
import MailContainer from '@containers/mail/MailContainer';
import CreateTenderContainer from '@containers/tender/crud/CreateTenderContainer';
import UpdateTransportOrderContainer from '@containers/transportOrder/crud/UpdateTransportOrderContainer';

import Collapsible from '@uicomponents/Collapsible';
import FilePreview from '@uicomponents/FilePreview';
import FileViewer from '@uicomponents/FileViewer';
import Loader from '@uicomponents/Loader';
import NoData from '@uicomponents/NoData';
import OptionsMenu from '@uicomponents/OptionsMenu';

import Alert from '@models/Alert';

import { formatAmount } from '@utils/moneyUtils';

import { TransportOrderPDFDownload } from './TransportOrderPDF';

class TransportOrderView extends Component {
  state = {
    filePreviewOpen: false,
    fileList: [],
    fileFocus: 0,
  };

  render() {
    const {
      t,
      platform,
      isPublic,
      user,
      onChange,
      createAlert,
      addToStack,
      transportOrder,
      isFetchingTransportOrder,
      animationHasEnded,
      acceptTransportOrder,
      cancelTransportOrder,
      deleteTransportOrder,
    } = this.props;

    const { filePreviewOpen, files, focussedFile } = this.state;
    if (isFetchingTransportOrder || !transportOrder) {
      return <Loader />;
    }

    return (
      <div className="transportOrder-view">
        <FileViewer
          t={t}
          isActive={filePreviewOpen}
          files={files}
          file={focussedFile}
          onClose={() => {
            this.setState({
              files: [],
              focussedFile: null,
              filePreviewOpen: false,
            });
          }}
        />

        <div className="information">
          <div className="flex-container justify-between">
            <div>
              <div className="flex-container justify-between">
                <h1 className="no-margin with-flair">
                  {transportOrder.name} <span>{transportOrder.status}</span>
                </h1>
              </div>
              <h3 className="no-margin">#{transportOrder.orderId}</h3>
            </div>
            <div className="self-center flex-container action-icons">
              {!isPublic &&
              platform.features?.map((feature) => feature.name)?.includes('tenders') &&
              transportOrder.tender == null &&
              transportOrder.status === 'accepted' ? (
                <div
                  className="action-icon margin"
                  onClick={(e) => {
                    e.preventDefault();

                    addToStack({
                      name: t('tender.create.header'),
                      component: <CreateTenderContainer transportOrder={{ ...transportOrder }} />,
                    });
                  }}
                >
                  <ReactSVG src="/icons/marketplace.svg" />
                </div>
              ) : null}

              <div
                className="action-icon margin"
                onClick={(e) => {
                  document.querySelector('.transportOrder-view .pdf-download').click();
                }}
              >
                <TransportOrderPDFDownload
                  key={transportOrder.orderId}
                  transportOrder={{ ...transportOrder }}
                  platform={platform}
                  className="pdf-download"
                />
                <ReactSVG src="/icons/pdf.svg" />
              </div>

              <div
                className="action-icon margin"
                onClick={(e) => {
                  e.preventDefault();

                  try {
                    navigator.clipboard.writeText(
                      `${
                        platform?.hostNames?.[0]?.name || window?.location?.origin
                      }/transportOrders/${transportOrder.id}/share/${
                        transportOrder?.shareSettings?.shareCode
                      }`
                    );

                    createAlert(
                      new Alert(
                        t('notification.copy.success.title'),
                        t('notification.copy.success.description'),
                        'success'
                      )
                    );
                  } catch (e) {
                    console.error(e)
                    createAlert(
                      new Alert(
                        t('notification.copy.error.title'),
                        t('notification.copy.error.description'),
                        'error'
                      )
                    );
                  }
                }}
              >
                <ReactSVG src="/icons/link.svg" />
              </div>
              {!isPublic && (
                <>
                  <div
                    className="action-icon margin"
                    onClick={(e) => {
                      e.preventDefault();
                      addToStack({
                        name: t('transportOrder.update.header'),
                        component: (
                          <UpdateTransportOrderContainer
                            transportOrder={{ ...transportOrder }}
                            callback={(result) => {
                              onChange('transportOrder', result.transportOrder || {});
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    <ReactSVG src="/icons/pencil.svg" />
                  </div>
                  <div className="action self-center margin">
                    {['requested', 'accepted'].includes(transportOrder.status) && (
                      <OptionsMenu
                        className="self-center"
                        items={[
                          {
                            name: t('form.cancel'),
                            onClick: (e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              cancelTransportOrder(transportOrder);
                            },
                          },
                          {
                            name: t('form.delete'),
                            onClick: (e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              deleteTransportOrder(transportOrder);
                            },
                          },
                        ]}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="description">
            <b>{t('transportOrder.description')}</b>:{' '}
            {transportOrder.description ? (
              <div>{transportOrder.description}</div>
            ) : (
              <NoData className="left">{t('transportOrder.description.notFound')}</NoData>
            )}
          </div>

          <div className="description">
            <b>{t('transportOrder.referenceDescription')}</b>: {transportOrder.referenceDescription}
          </div>
          {transportOrder.mailMessage && (
            <div className="mail">
              <b>{t('transportOrder.fromMail')}</b>:{' '}
              <span
                className="as-link"
                onClick={(e) => {
                  e.preventDefault();

                  addToStack({
                    name: transportOrder.mailMessage.subject,
                    component: <MailContainer mail={transportOrder.mailMessage} />,
                  });
                }}
              >
                {transportOrder.mailMessage.subject}
              </span>
            </div>
          )}

          <div className="deliveryTerms">
            <b>{t('transportOrder.deliveryTerms')}</b>:{' '}
            {t(`deliveryTerms.${transportOrder.deliveryTerms}`)}
          </div>

          <div className="client">
            <b>{t('transportOrder.customer')}</b>:{` `}
            <CustomerView customer={transportOrder.customer} addToStack={addToStack} />
          </div>
          <div className="value">
            <b>{t('transportOrder.value')}</b>:
            {transportOrder.value
              ? ` ${
                  transportOrder.value.currency ? transportOrder.value.currency.sign : '€'
                }${formatAmount(
                  transportOrder.value.amount,
                  2,
                  user?.language?.decimalDelimiter || '.',
                  user?.language?.thousandDelimiter || ','
                )}`
              : ` ${t('transportOrder.value.notFound')}`}
          </div>
          <b>{t('transportOrder.attachments')}</b>
          <div className="files">
            {transportOrder.documents.length > 0 ? (
              transportOrder.documents.map((attachment) => (
                <div
                  key={`attachment-${attachment.id}`}
                  className="file"
                  onClick={() => {
                    this.setState({
                      filePreviewOpen: true,
                      files: transportOrder.documents.map((document) => document.entity.file),
                      focussedFile: attachment.entity.file,
                    });
                  }}
                >
                  <FilePreview file={attachment.entity.file} />
                </div>
              ))
            ) : (
              <NoData className="left">{t('transportOrder.attachments.notFound')}</NoData>
            )}
          </div>
        </div>
        <div className="consignments">
          {transportOrder.consignments
            .map((association) => association.entity)
            .map((consignment, index) => (
              <Collapsible
                key={`consignment-${consignment.id}`}
                name={`${t('consignment.header', { count: index + 1 })} ${
                  consignment.name ? ` - ${consignment.name}` : null
                }`}
                className="collapsible-element"
                defaultIsOpen={true}
                childStyle={{ padding: '10px 10px 10px 40px' }}
              >
                <ConsignmentView
                  {...this.props}
                  key={consignment.id}
                  consignment={consignment}
                  animationHasEnded={animationHasEnded}
                />
              </Collapsible>
            ))}
        </div>
        <Collapsible
          name={`${t('notes.header')}${
            transportOrder.notes.length > 0 ? ` (${transportOrder.notes.length})` : ''
          }`}
          className="collapsible-element"
          defaultIsOpen={false}
        >
          <NotesView notes={transportOrder.notes} />
        </Collapsible>
        <Collapsible
          name={`${t('contactDetails.header')}${
            transportOrder.contactDetails.length > 0
              ? ` (${transportOrder.contactDetails.length})`
              : ''
          }`}
          className="collapsible-element"
          defaultIsOpen={false}
        >
          <ContactDetailsView contactDetails={transportOrder.contactDetails} />
        </Collapsible>
        {transportOrder.status === 'requested' && !isPublic && (
          <div className="action-bar">
            <button
              className="light"
              onClick={(e) => {
                e.preventDefault();

                cancelTransportOrder(transportOrder);
              }}
            >
              {t('form.cancel')}
            </button>
            <div className="buttons">
              {!transportOrder.quote &&
                platform.features?.map((feature) => feature.name)?.includes('financial') && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();

                      addToStack({
                        name: t('quote.create'),
                        fullWidth: true,
                        component: (
                          <QuoteFormContainer
                            administration={transportOrder.administration}
                            targetBusiness={transportOrder.customer.business}
                            transportOrder={transportOrder}
                            callback={(e) => {
                              getTransportOrder(transportOrder.id);
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    {t('quote.create')}
                  </button>
                )}
              {transportOrder.quote && (
                <button
                  onClick={(e) => {
                    e.preventDefault();

                    addToStack({
                      name: transportOrder.quote.name,
                      component: <QuoteFormContainer quote={transportOrder.quote} />,
                    });
                  }}
                >
                  {t('quote.view')}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();

                  acceptTransportOrder(transportOrder);
                }}
              >
                {t('form.accept')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation('translation')(TransportOrderView);
